import { CountdownTimer } from '@/components/atom/CountdownTimer';
import DailyMissionTask from '@/components/mission-hub/modals/DailyMissionTask';
import { Modal } from '@/components/molecule/Modal';
import { useAlerts } from '@/hooks/alerts';
import { useEvents } from '@/hooks/events';
import { useTranslation } from '@/hooks/support/translate';
import { useWallet } from '@/hooks/wallet';
import { appEventsAtom, appSignatureAtom, Events } from '@/state/app';
import { getQuiz, postQuizAnswer, Quiz } from '@/utils/http/quiz';
import classNames from 'classnames';
import { add } from 'date-fns';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { MissionHubTask } from '../MissionHubTask';

export interface DailyMissionProps {
    signup: () => void;
    points: number;
}

const getEndTime = (startTime: Date, duration: number) => add(startTime, { minutes: duration });

export const DailyMission = (props: DailyMissionProps) => {
    const { t } = useTranslation();
    const { pushEvent } = useEvents();
    const { alertSuccess, alertError } = useAlerts();
    const { walletState } = useWallet();
    const signature = useRecoilValue(appSignatureAtom);
    const appEvents = useRecoilValue(appEventsAtom);

    const [quiz, setQuiz] = useState<Quiz | null>(null);
    const [state, setState] = useState({
        modalVisible: false,
    });
    const isSigned = walletState.selectedAddress && signature.address === walletState.selectedAddress;
    const startTime = quiz?.start ? new Date(quiz.start) : undefined;
    const endTime = startTime && quiz?.duration ? getEndTime(startTime, quiz?.duration) : undefined;
    const nextDate = quiz?.date_till ? new Date(quiz.date_till!) : undefined;
    const hasExpired = endTime ? new Date() > endTime : false;
    const awardedPoints = props.points ?? 0;

    // Load initial quiz
    useEffect(() => {
        if (isSigned) {
            // @ts-ignore
            fetchQuiz(false);
        } else {
            setQuiz(null);
        }
    }, [signature, appEvents.refreshPoints]);

    const fetchQuiz = async (startQuiz: boolean) => {
        if (isSigned) {
            const quiz = await getQuiz(signature.address!, signature.signature!, startQuiz);
            setQuiz(quiz);
        }
    };

    const handleQuizStart = async () => {
        if (!isSigned) {
            await props.signup();
            return;
        }
        if (!quiz?.start) {
            // @ts-ignore
            await fetchQuiz(true);
        }
        setState({ ...state, modalVisible: true });
    };

    const handleAnswer = async (chosenAnswer: string) => {
        const answerBody = {
            address: walletState.selectedAddress!,
            signature: signature.signature,
            answer: chosenAnswer,
            quiz_id: quiz!._id,
        };

        // @ts-ignore
        const response = await postQuizAnswer(answerBody);
        if (response.success) {
            pushEvent(Events.refreshPoints);
            setState({ ...state, modalVisible: false });
            alertSuccess('Answer submitted!');
        } else {
            alertError('Something went wrong. Please try again later.');
        }
    };

    const StartQuiz = () => {
        return (
            <MissionHubTask
                mission={{
                    _id: 'DAILY-MISSION',
                    title: t('modal.pointsSystem.quiz.dailyMission'),
                    description: t('modal.pointsSystem.quiz.dailyMissionDescription'),
                    timestamp: Date.now(),
                }}
                points={awardedPoints}
                onClick={handleQuizStart}
                isDisabled={!quiz && !!isSigned}
                ctaLabel={t('modal.pointsSystem.quiz.startMission')}
            />
        );
    };

    const NextQuiz = () => {
        return (
            <div className="relative flex flex-col p-4 pt-6 rounded-lg first-line:ps-card card card__border bg-grey-50">
                <div className="flex flex-col justify-between h-full space-y-8 text-center">
                    <span className="text-white">{t('modal.pointsSystem.quiz.dailyMission')}</span>
                    <span
                        className={classNames('t__heading--h5', {
                            'text-grey-10': awardedPoints == 0,
                            'text-[#ffc635]': awardedPoints > 0,
                        })}
                    >
                        {awardedPoints}
                    </span>
                    <span className="t__caption--c1">{t('modal.pointsSystem.quiz.nextMissionDescription')}</span>
                    <span className="t__caption--c1">{t('modal.pointsSystem.quiz.nextMissionIn')}</span>
                    <CountdownTimer className="p-2 text-white rounded-md bg-grey-50 min-h-[44px]" endTime={nextDate} />
                </div>
            </div>
        );
    };

    const QuizModal = () => {
        return (
            <Modal
                isVisible={!!quiz && state.modalVisible}
                onClose={() => {
                    fetchQuiz(false);
                    setState({ ...state, modalVisible: false });
                }}
                bodyPadding="p-0"
                modalWidth="w-full md:w-[32rem]"
                arrowSide="right-edge"
            >
                <DailyMissionTask
                    quiz={quiz!}
                    // @ts-ignore
                    handleAnswer={handleAnswer}
                    endTime={endTime}
                />
            </Modal>
        );
    };

    return (
        <>
            <QuizModal />
            {quiz?.end || hasExpired ? <NextQuiz /> : <StartQuiz />}
        </>
    );
};
